/* --------------- *** Constants *** --------------- */

const SELECTOR_GRID = "#event-grid";
const SELECTOR_ITEM = "[data-event-index]";
const SELECTOR_NAV = ".js-events-pagination";
const SELECTOR_LOAD_MORE = ".js-events-load-more";
const SELECTOR_LOAD_MORE_BTN = ".js-events-btn-load-more";

const PAGINATION_STYLE_DEFAULT = "default";
const PAGINATION_STYLE_LOAD_MORE = "load_more";

const DEFAULT_PER_PAGE = 9;

const ARROW_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true" focusable="false" class="pagination__arrow-svg">
  <path d="M2.40039 7.2001H12.0004M12.0004 7.2001L8.40039 3.6001M12.0004 7.2001L8.40039 10.8001" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

/* --------------- *** Events Grid Pagination *** --------------- */

/**
 * Client-side paginator for section-block-based event grids.
 *
 * Renders all cards server-side, then shows/hides per page via JS.
 * Supports numbered pagination or cumulative load more.
 */
class EventsGridPagination extends HTMLElement {
  constructor() {
    super();

    this.grid = null;
    this.nav = null;
    this.loadMoreWrap = null;
    this.loadMoreBtn = null;
    this.items = [];
    this.perPage = DEFAULT_PER_PAGE;
    this.totalPages = 1;
    this.currentPage = 1;
    this.paginationStyle = PAGINATION_STYLE_DEFAULT;
    this.handleNavClickBound = this.handleNavClick.bind(this);
    this.handlePopStateBound = this.handlePopState.bind(this);
    this.handleLoadMoreBound = this.handleLoadMoreClick.bind(this);
  }

  /* --------------- *** Lifecycle *** --------------- */

  connectedCallback() {
    this.grid = this.querySelector(SELECTOR_GRID);
    this.nav = this.querySelector(SELECTOR_NAV);
    this.loadMoreWrap = this.querySelector(SELECTOR_LOAD_MORE);
    this.loadMoreBtn = this.querySelector(SELECTOR_LOAD_MORE_BTN);
    this.items = [...this.querySelectorAll(SELECTOR_ITEM)];
    this.perPage = parseInt(this.dataset.perPage, 10) || DEFAULT_PER_PAGE;
    this.paginationStyle = this.dataset.paginationStyle || PAGINATION_STYLE_DEFAULT;
    this.totalPages = Math.ceil(this.items.length / this.perPage);
    this.currentPage = this.getPageFromUrl();

    this.render();
    this.bindEvents();
  }

  disconnectedCallback() {
    this.nav?.removeEventListener("click", this.handleNavClickBound);
    this.loadMoreBtn?.removeEventListener("click", this.handleLoadMoreBound);
    window.removeEventListener("popstate", this.handlePopStateBound);
  }

  /* --------------- *** URL Helpers *** --------------- */

  getPageFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const raw = parseInt(params.get("page"), 10);

    if (!Number.isFinite(raw) || raw < 1) return 1;
    if (raw > this.totalPages) return this.totalPages;

    return raw;
  }

  updateUrl(page) {
    const url = new URL(window.location.href);

    if (page <= 1) {
      url.searchParams.delete("page");
    } else {
      url.searchParams.set("page", page);
    }

    history.pushState(null, "", url.toString());
  }

  /* --------------- *** Rendering *** --------------- */

  render() {
    this.showPage(this.currentPage);
    this.renderControls();
  }

  showPage(page) {
    if (this.isLoadMore()) {
      const visibleCount = page * this.perPage;

      this.items.forEach((item, i) => {
        item.hidden = i >= visibleCount;
      });
    } else {
      const start = (page - 1) * this.perPage;
      const end = start + this.perPage;

      this.items.forEach((item, i) => {
        item.hidden = i < start || i >= end;
      });
    }

    this.currentPage = page;
  }

  renderControls() {
    if (this.isLoadMore()) {
      this.renderLoadMore();

      if (this.nav) {
        this.nav.innerHTML = "";
        this.nav.hidden = true;
      }
    } else {
      this.renderNav();

      if (this.loadMoreWrap) {
        this.loadMoreWrap.hidden = true;
      }
    }
  }

  renderNav() {
    if (!this.nav) return;

    if (this.totalPages <= 1) {
      this.nav.innerHTML = "";
      return;
    }

    const track = document.createElement("div");
    track.classList.add("events__pagination-track");

    if (this.currentPage > 1) {
      track.appendChild(this.createArrow("prev", this.currentPage - 1));
    }

    const pagesList = document.createElement("ul");
    pagesList.classList.add("list-unstyled", "events__pagination-pages");

    for (let i = 1; i <= this.totalPages; i++) {
      pagesList.appendChild(this.createPageItem(i));
    }

    track.appendChild(pagesList);

    if (this.currentPage < this.totalPages) {
      track.appendChild(this.createArrow("next", this.currentPage + 1));
    }

    this.nav.innerHTML = "";
    this.nav.appendChild(track);
    this.nav.hidden = false;
  }

  renderLoadMore() {
    if (!this.loadMoreWrap) return;

    const hasMore = this.currentPage < this.totalPages;

    this.loadMoreWrap.hidden = !hasMore;
    this.loadMoreBtn?.classList.toggle("is-loading", false);
  }

  /* --------------- *** Nav Element Builders *** --------------- */

  createPageItem(page) {
    const li = document.createElement("li");

    if (page === this.currentPage) {
      const span = document.createElement("span");

      span.setAttribute("aria-current", "page");
      span.setAttribute("aria-label", `Page ${page}`);
      span.classList.add("pagination__current");
      span.textContent = page;
      li.appendChild(span);
    } else {
      const a = document.createElement("a");

      a.href = "#";
      a.title = `Page ${page}`;
      a.textContent = page;
      a.dataset.page = page;
      li.appendChild(a);
    }

    return li;
  }

  createArrow(direction, targetPage) {
    const link = document.createElement("a");

    link.href = "#";
    link.classList.add(
      "pagination__prev-next",
      `pagination__prev-next--${direction}`,
      "events__pagination-arrow",
    );
    link.setAttribute("aria-label", direction === "prev" ? "Previous page" : "Next page");
    link.dataset.page = targetPage;
    link.innerHTML = ARROW_SVG;

    return link;
  }

  /* --------------- *** Event Listeners *** --------------- */

  bindEvents() {
    this.nav?.addEventListener("click", this.handleNavClickBound);
    this.loadMoreBtn?.addEventListener("click", this.handleLoadMoreBound);
    window.addEventListener("popstate", this.handlePopStateBound);
  }

  handleNavClick(event) {
    const link = event.target.closest("a[data-page]");

    if (!link) return;

    event.preventDefault();

    const page = parseInt(link.dataset.page, 10);

    if (!Number.isFinite(page) || page === this.currentPage) return;

    this.goToPage(page);
  }

  handleLoadMoreClick(event) {
    event.preventDefault();

    if (this.currentPage >= this.totalPages) return;

    this.goToPage(this.currentPage + 1);
  }

  handlePopState() {
    const page = this.getPageFromUrl();

    if (page === this.currentPage) return;

    this.showPage(page);
    this.renderControls();
  }

  /* --------------- *** Navigation *** --------------- */

  goToPage(page) {
    this.showPage(page);
    this.renderControls();
    this.updateUrl(page);

    if (!this.isLoadMore()) {
      this.scrollToTop();
    }
  }

  scrollToTop() {
    const section = this.closest(".shopify-section");

    if (!section) return;

    const headerHeight = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue("--header-height"),
    );
    const headerOffset = Number.isFinite(headerHeight) ? headerHeight : 0;
    const top = section.getBoundingClientRect().top + window.scrollY - headerOffset;

    window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  }

  isLoadMore() {
    return this.paginationStyle === PAGINATION_STYLE_LOAD_MORE;
  }
}

/* --------------- *** Register *** --------------- */

if (!customElements.get("events-grid-pagination")) {
  customElements.define("events-grid-pagination", EventsGridPagination);
}
